import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    FaUsers, FaThLarge, FaGlobe, FaPen, FaFileAlt,
    FaStar, FaMoneyBill, FaSignOutAlt, FaBars, FaGraduationCap,
    FaTimes, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';

import './sidemenu.css';
import useWindowDimensions from '../../utils/WindowDimension';
import useOnClickOutside from "../../utils/useOnClickOutside";
import { MenuLogo, MenuMobLogo, DrawerLogo, CollapsedIcon } from './SideMenu.Styled';
import { useAuth } from '../../context/AuthContext';
import iconSvg from '../../assets/icon.svg';

interface NavItem {
    path: string;
    icon: React.ReactNode;
    label: string;
    matchPaths?: string[];
}

const NAV_ITEMS: NavItem[] = [
    { path: "/", icon: <FaUsers />, label: "Accounts" },
    { path: "/platform", icon: <FaThLarge />, label: "Platform" },
    { path: "/localization", icon: <FaGlobe />, label: "Localization" },
    { path: "/blog", icon: <FaPen />, label: "Blog", matchPaths: ["/post"] },
    { path: "/templates", icon: <FaFileAlt />, label: "CV Templates", matchPaths: ["edit-template"] },
    { path: "/reviews", icon: <FaStar />, label: "Reviews" },
    { path: "/course", icon: <FaGraduationCap />, label: "Course", matchPaths: ["/lesson"] },
    { path: "/payments", icon: <FaMoneyBill />, label: "Payments" },
];

const MOBILE_BREAKPOINT = 1024;

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

const SideMenu = () => {
    const { logout } = useAuth();
    const { width } = useWindowDimensions();
    const { pathname } = useLocation();

    const [showMenu, setShowMenu] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return saved === 'true';
    });
    const menuRef = useRef<HTMLDivElement>(null);

    const isMobileView = width <= MOBILE_BREAKPOINT;

    useOnClickOutside(menuRef, () => setShowMenu(false));

    useEffect(() => {
        document.body.classList.toggle('menu-open', showMenu);
        return () => document.body.classList.remove('menu-open');
    }, [showMenu]);

    useEffect(() => {
        if (!isMobileView) {
            document.body.classList.toggle('sidebar-collapsed', isCollapsed);
            document.body.classList.toggle('sidebar-expanded', !isCollapsed);
        } else {
            document.body.classList.remove('sidebar-collapsed', 'sidebar-expanded');
        }
    }, [isCollapsed, isMobileView]);

    useEffect(() => {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
    }, [isCollapsed]);

    useEffect(() => {
        setShowMenu(false);
    }, [pathname]);

    const toggleMenu = useCallback(() => setShowMenu(prev => !prev), []);
    const closeMenu = useCallback(() => setShowMenu(false), []);
    const toggleCollapse = useCallback(() => setIsCollapsed(prev => !prev), []);

    const handleLogout = useCallback(async () => {
        await logout();
    }, [logout]);

    const isActive = useCallback((item: NavItem): boolean => {
        if (pathname === item.path) return true;
        return item.matchPaths?.some(match => pathname.includes(match)) ?? false;
    }, [pathname]);

    const renderNavLinks = (showLabels = true) => (
        <div className="nav-links">
            {NAV_ITEMS.map((item) => (
                <Link
                    key={item.path}
                    to={item.path}
                    className={`header-menu-button ${isActive(item) ? 'active' : ''}`}
                    title={showLabels ? undefined : item.label}
                >
                    {item.icon}
                    {showLabels && <span>{item.label}</span>}
                </Link>
            ))}
        </div>
    );

    const renderLogoutButton = (showLabel = true) => (
        <div className="logout-button">
            <button
                className="header-menu-button"
                onClick={handleLogout}
                title={showLabel ? undefined : "Logout"}
            >
                <FaSignOutAlt />
                {showLabel && <span>Logout</span>}
            </button>
        </div>
    );

    if (isMobileView) {
        return (
            <>
                <div className="header__container">
                    <div className="logo__container">
                        <MenuMobLogo src={require('../../assets/logo1.png')} alt="Logo" />
                        <button
                            className="header-menu"
                            onClick={toggleMenu}
                            aria-expanded={showMenu}
                            aria-label="Toggle navigation menu"
                        >
                            <FaBars size={20} />
                        </button>
                    </div>
                </div>

                <div
                    className={`sidebar-overlay ${showMenu ? 'active' : ''}`}
                    onClick={closeMenu}
                    aria-hidden="true"
                />

                <div
                    ref={menuRef}
                    className={`menu ${showMenu ? 'open' : ''}`}
                    role="navigation"
                    aria-label="Main navigation"
                >
                    <div className="drawer-header">
                        <DrawerLogo src={require('../../assets/logo1.png')} alt="Logo" />
                        <button
                            className="close-button"
                            onClick={closeMenu}
                            aria-label="Close menu"
                        >
                            <FaTimes size={16} />
                        </button>
                    </div>
                    {renderNavLinks()}
                    {renderLogoutButton()}
                </div>
            </>
        );
    }

    return (
        <div className={`side__container ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="desktop-logo-area">
                {isCollapsed
                    ? <CollapsedIcon src={iconSvg} alt="Icon" />
                    : <MenuLogo src={require('../../assets/logo1.png')} alt="Logo" />
                }
            </div>

            <button
                className="collapse-toggle"
                onClick={toggleCollapse}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
                {isCollapsed ? <FaChevronRight size={12} /> : <FaChevronLeft size={12} />}
            </button>

            {renderNavLinks(!isCollapsed)}
            {renderLogoutButton(!isCollapsed)}
        </div>
    );
};

export default SideMenu;